import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  updateCategoryName,
  getCategoriesWithGenericsAndBrands,
  deleteCategoryById,
} from "../../services/categoryService";
import {
  updateGenericName,
  deleteGenericName,
} from "../../services/genericNameService";
import dayjs from "dayjs";
import { getPaginatedBrandDrugs } from "../../pages/networkCalls";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import CategoryEditModal from "./CategoryEditModal";
import AddCategoryModal from "./AddCategoryModal";
import { addNewCategoryName } from "../../services/categoryService";
import GenericEditModal from "./GenericEditModal";
import BrandDrugEditModal from "./BrandDrugEditModal";
import AddBrandDrugModal from "./AddBrandDrugModal";
import { addNewBrandDrugName } from "../../services/brandDrugNameService";
import AddGenericNameModal from "./AddGenericNameModal";
import { addNewGenericName } from "../../services/genericNameService";
import { ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import UseAnimations from "react-useanimations";
import editIcon from "react-useanimations/lib/edit";
import trash2 from "react-useanimations/lib/trash2";
import plusIcon from "react-useanimations/lib/plusToX";
import {
  updateBrandDrug,
  deleteBrandDrug,
} from "../../services/brandDrugNameService";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Input } from "../ui/input";

interface BrandDrug {
  brand_drug_id: string;
  brand_drug_name: string;
  brand_drug_contents: string;
  brand_drug_formulation: string;
  brand_drug_last_used_at: string;
  brand_drug_usage_count: number;
}
interface GenericName {
  generic_name_id: string;
  generic_name_name: string;
  generic_name_last_used_at: string;
  generic_name_usage_count: number;
  brand_drug_names: BrandDrug[];
}
interface Category {
  category_id: string;
  category_name: string;
  category_last_used_at: string;
  category_usage_count: number;
  generic_names: GenericName[];
}
interface BrandDrugEditType {
  brand_drug_name: string;
  brand_drug_contents: string;
  brand_drug_formulation: string;
}
// Remove initialData, data will be fetched from API

const PrescriptionTable: React.FC = () => {
  // State for AddBrandDrugModal
  const [addBrandDrugModalOpen, setAddBrandDrugModalOpen] = useState(false);
  const [selectedGenericId, setSelectedGenericId] = useState<string | null>(
    null
  );
  // State for AddGenericNameModal
  const [addGenericModalOpen, setAddGenericModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );

  // Handler for adding generic name
  const handleAddGenericName = async (
    categoryId: string,
    genericName: string
  ) => {
    try {
      await addNewGenericName({ genericName, categoryId });
      toast.success(`Generic name '${genericName}' added!`);
      setAddGenericModalOpen(false);
      setSelectedCategoryId(null);
      await fetchData();
    } catch (error) {
      toast.error("Failed to add generic name");
    }
  };
  // For popover state and input for brand drug delete confirmation
  const [deletePopover, setDeletePopover] = useState<{ [id: string]: boolean }>(
    {}
  );
  const [deleteInput, setDeleteInput] = useState<{ [id: string]: string }>({});
  const [genericDeletePopover, setGenericDeletePopover] = useState<{
    [id: string]: boolean;
  }>({});
  const [genericDeleteInput, setGenericDeleteInput] = useState<{
    [id: string]: string;
  }>({});
  const [data, setData] = useState<Category[]>([]);
  // TODO: Replace with actual token retrieval logic

  // Add missing state for expandedCategories and expandedGenerics
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [expandedGenerics, setExpandedGenerics] = useState<Set<string>>(
    new Set()
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>({});

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGeneric = (id: string) => {
    setExpandedGenerics((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  async function fetchData() {
    try {
      // Example: fetch paginated brand drugs, replace with actual API if needed
      const result = await getCategoriesWithGenericsAndBrands();
      setData(result || []);
    } catch (error) {
      console.error("Failed to fetch prescription data", error);
    }
  }
  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  // Handle edit modal field changes
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm((prev: any) => ({ ...prev, [name]: value }));
  };

  // Start editing an item
  const startEdit = (
    type: "category" | "generic_name" | "brand_drug",
    item: any
  ) => {
    setEditing({ type, id: item[`${type}_id`] });

    if (type === "category") {
      setEditForm({ category_name: item.category_name });
    } else if (type === "generic_name") {
      setEditForm({ generic_name_name: item.generic_name_name });
    } else if (type === "brand_drug") {
      setEditForm({
        brand_drug_name: item.brand_drug_name,
        brand_drug_contents: item.brand_drug_contents,
        brand_drug_formulation: item.brand_drug_formulation,
      });
    }
    setModalOpen(true);
  };
  // Save edits
  const saveEdit = async (id: string, value: string | BrandDrugEditType) => {
    try {
      if (!editing) return;
      if (editing.type === "category") {
        // Call the API to update category name
        await updateCategoryName({ id, name: value as string });
        toast.success("Category updated successfully");
      } else if (editing.type === "generic_name") {
        await updateGenericName({ id, name: value as string });
        toast.success("Generic name updated successfully");
      } else if (editing.type === "brand_drug") {
        await updateBrandDrug({
          id,
          name: (value as BrandDrugEditType).brand_drug_name,
          contents: (value as BrandDrugEditType).brand_drug_contents,
          formulationType: (value as BrandDrugEditType).brand_drug_formulation,
        });
        toast.success("Brand drug updated successfully");
        // Update brand drug
        // Example: await updateBrandDrugName(id, value as { brand_drug_name: string; brand_drug_contents: string; brand_drug_formulation: string });
      }
      setModalOpen(false);
      setEditing(null);
      setEditForm({});
      // Optionally refresh data
      await fetchData();
    } catch (error) {
      console.error("Failed to save edit", error);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setModalOpen(false);
    setEditing(null);
    setEditForm({});
  };

  // Delete item
  const deleteItem = async (
    type: "category" | "generic_name" | "brand_drug",
    id: string
  ) => {
    try {
      if (type === "category") {
        await deleteCategoryById(id);
      } else if (type === "generic_name") {
        await deleteGenericName(id);
      } else if (type === "brand_drug") {
        await deleteBrandDrug(id);
      }
      // Optionally refresh data
      await fetchData();
    } catch (error) {
      console.error(`Failed to delete ${type}`, error);
    }
  };
  // Main render
  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Prescription Categories</h2>
        <div className="flex items-center gap-2">
          <AddCategoryModal
            open={addCategoryModalOpen}
            onOpenChange={setAddCategoryModalOpen}
            onSave={async (categoryName) => {
              try {
                const response = await addNewCategoryName(categoryName);

                toast.success(`Category '${categoryName}' added!`);
                setAddCategoryModalOpen(false);
                fetchData();
              } catch (error) {
                console.log(error);
                toast.error("Failed to add category");
              }
            }}
            onCancel={() => setAddCategoryModalOpen(false)}
            trigger={
              <div className="relative group">
                <Button
                  size="icon"
                  variant="secondary"
                  aria-label="Add Category"
                  onClick={() => setAddCategoryModalOpen(true)}
                >
                  <UseAnimations
                    animation={plusIcon}
                    size={24}
                    style={{ color: "#2563eb" }}
                  />
                </Button>
                <span
                  className="absolute left-1/2 -translate-x-1/2 mt-2 px-2 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap"
                  style={{ top: "100%" }}
                >
                  Add Category
                </span>
              </div>
            }
          />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card shadow-sm">
        <Table className="min-w-full text-sm">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead className="py-3 px-4">Name</TableHead>
              <TableHead className="py-3 px-4">Last Used</TableHead>
              <TableHead className="py-3 px-4">Usage Count</TableHead>
              <TableHead className="py-3 px-4">Contents</TableHead>
              <TableHead className="py-3 px-4">Type</TableHead>
              <TableHead className="py-3 px-4">Formulation</TableHead>
              <TableHead className="py-3 px-4">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map((cat) => (
              <React.Fragment key={cat.category_id}>
                <TableRow className="hover:bg-muted/30">
                  <TableCell className="py-2 px-4 font-semibold flex items-center gap-2">
                    <button
                      onClick={() => toggleCategory(cat.category_id)}
                      className="focus:outline-none"
                      aria-label="Expand category"
                    >
                      {expandedCategories.has(cat.category_id) ? (
                        <ChevronDownIcon />
                      ) : (
                        <ChevronRightIcon />
                      )}
                    </button>
                    {cat.category_name}
                  </TableCell>
                  <TableCell className="py-2 px-4">
                    {cat.category_last_used_at
                      ? dayjs(cat.category_last_used_at).format("MMM D, YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell className="py-2 px-4">
                    {cat.category_usage_count}
                  </TableCell>
                  <TableCell className="py-2 px-4">-</TableCell>
                  <TableCell className="py-2 px-4">
                    <Badge
                      variant="default"
                      className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200"
                    >
                      Category
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 px-4">-</TableCell>
                  <TableCell className="py-2 px-4">
                    <div className="flex items-center gap-2">
                      {/* PlusToX button to add generic name */}
                      <div className="relative group">
                        <button
                          type="button"
                          aria-label="Add Generic Name"
                          className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                          onClick={() => {
                            setSelectedCategoryId(cat.category_id);
                            setAddGenericModalOpen(true);
                          }}
                        >
                          <UseAnimations
                            animation={plusIcon}
                            size={22}
                            style={{ color: "#22c55e" }}
                          />
                        </button>
                        <span
                          className="absolute left-1/2 -translate-x-1/2 mt-2 px-2 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[999] whitespace-nowrap"
                          style={{ bottom: "100%" }}
                        >
                          Add Generic Name
                        </span>
                      </div>
                      <CategoryEditModal
                        open={
                          modalOpen &&
                          editing?.type === "category" &&
                          editing?.id === cat.category_id
                        }
                        onOpenChange={(open) => {
                          setModalOpen(open);
                          if (!open) cancelEdit();
                        }}
                        value={editForm.category_name || ""}
                        categoryId={cat.category_id}
                        onChange={handleEditChange}
                        onSave={(categoryId, categoryName) => {
                          // You can now use both values here
                          // Example: await updateCategoryName(categoryId, categoryName);
                          saveEdit(categoryId, categoryName);
                        }}
                        onCancel={cancelEdit}
                        trigger={
                          <button
                            type="button"
                            aria-label="Edit"
                            onClick={() => startEdit("category", cat)}
                            className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                          >
                            <UseAnimations
                              animation={editIcon}
                              size={22}
                              style={{ color: "#2563eb" }}
                            />
                          </button>
                        }
                      />
                      <Popover
                        open={!!deletePopover[cat.category_id]}
                        onOpenChange={(open) => {
                          setDeletePopover((prev) => ({
                            ...prev,
                            [cat.category_id]: open,
                          }));
                          if (!open) {
                            setDeleteInput((prev) => ({
                              ...prev,
                              [cat.category_id]: "",
                            }));
                          }
                        }}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label="Delete"
                            className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                            onClick={() =>
                              setDeletePopover((prev) => ({
                                ...prev,
                                [cat.category_id]: true,
                              }))
                            }
                          >
                            <UseAnimations
                              animation={trash2}
                              size={22}
                              style={{ color: "#dc2626" }}
                            />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72">
                          <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                            Type{" "}
                            <span className="font-bold">
                              {cat.category_name}
                            </span>{" "}
                            to confirm delete:
                          </div>
                          <Input
                            value={deleteInput[cat.category_id] || ""}
                            onChange={(e) =>
                              setDeleteInput((prev) => ({
                                ...prev,
                                [cat.category_id]: e.target.value,
                              }))
                            }
                            placeholder="Category name"
                            className="mb-2"
                            autoFocus
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setDeletePopover((prev) => ({
                                  ...prev,
                                  [cat.category_id]: false,
                                }));
                                setDeleteInput((prev) => ({
                                  ...prev,
                                  [cat.category_id]: "",
                                }));
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={
                                deleteInput[cat.category_id] !==
                                cat.category_name
                              }
                              onClick={async () => {
                                if (
                                  cat.generic_names &&
                                  cat.generic_names.length > 0
                                ) {
                                  toast.error(
                                    "Cannot delete Category, please delete all associated Generic names first and try again"
                                  );
                                  setDeletePopover((prev) => ({
                                    ...prev,
                                    [cat.category_id]: false,
                                  }));
                                  setDeleteInput((prev) => ({
                                    ...prev,
                                    [cat.category_id]: "",
                                  }));
                                  return;
                                } else {
                                  try {
                                    await deleteCategoryById(cat.category_id);
                                    toast.success(
                                      "Category deleted successfully"
                                    );
                                    setDeletePopover((prev) => ({
                                      ...prev,
                                      [cat.category_id]: false,
                                    }));
                                    setDeleteInput((prev) => ({
                                      ...prev,
                                      [cat.category_id]: "",
                                    }));
                                    await fetchData();
                                  } catch (error) {
                                    toast.error("Failed to delete category");
                                  }
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      {/* AddGenericNameModal for this category */}
                      {selectedCategoryId === cat.category_id && (
                        <AddGenericNameModal
                          open={addGenericModalOpen}
                          onOpenChange={(open) => {
                            setAddGenericModalOpen(open);
                            if (!open) setSelectedCategoryId(null);
                          }}
                          onSave={(genericName) => {
                            if (selectedCategoryId) {
                              handleAddGenericName(
                                selectedCategoryId,
                                genericName
                              );
                            }
                          }}
                          onCancel={() => {
                            setAddGenericModalOpen(false);
                            setSelectedCategoryId(null);
                          }}
                          trigger={<></>}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {/* Render generics if expanded */}
                {expandedCategories.has(cat.category_id) &&
                  cat.generic_names.map((gen) => (
                    <React.Fragment key={gen.generic_name_id}>
                      <TableRow className="bg-gray-50 dark:bg-gray-900/30">
                        <TableCell className="py-2 px-8 font-semibold flex items-center gap-2">
                          <button
                            onClick={() => toggleGeneric(gen.generic_name_id)}
                            className="focus:outline-none"
                            aria-label="Expand generic"
                          >
                            {expandedGenerics.has(gen.generic_name_id) ? (
                              <ChevronDownIcon />
                            ) : (
                              <ChevronRightIcon />
                            )}
                          </button>
                          {gen.generic_name_name}
                        </TableCell>
                        <TableCell className="py-2 px-4">
                          {gen.generic_name_last_used_at
                            ? dayjs(gen.generic_name_last_used_at).format(
                                "MMM D, YYYY"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell className="py-2 px-4">
                          {gen.generic_name_usage_count}
                        </TableCell>
                        <TableCell className="py-2 px-4">-</TableCell>
                        <TableCell className="py-2 px-4">
                          <Badge
                            variant="secondary"
                            className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200"
                          >
                            Generic Type
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 px-4">-</TableCell>
                        <TableCell className="py-2 px-4">
                          <div className="flex items-center gap-2">
                            {/* PlusToX button to add brand drug name */}
                            <div className="relative group">
                              <button
                                type="button"
                                aria-label="Add Brand Drug Name"
                                className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                                onClick={() => {
                                  setSelectedGenericId(gen.generic_name_id);
                                  setAddBrandDrugModalOpen(true);
                                }}
                              >
                                <UseAnimations
                                  animation={plusIcon}
                                  size={22}
                                  style={{ color: "#eab308" }}
                                />
                              </button>
                              <span
                                className="absolute left-1/2 -translate-x-1/2 mt-2 px-2 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[999] whitespace-nowrap"
                                style={{ bottom: "100%" }}
                              >
                                Add Brand Drug Name
                              </span>
                            </div>
                            <GenericEditModal
                              open={
                                modalOpen &&
                                editing?.type === "generic_name" &&
                                editing?.id === gen.generic_name_id
                              }
                              onOpenChange={(open) => {
                                setModalOpen(open);
                                if (!open) cancelEdit();
                              }}
                              values={{
                                generic_name_name:
                                  editForm.generic_name_name || "",
                              }}
                              genericNameId={gen.generic_name_id}
                              onChange={handleEditChange}
                              onSave={(genericNameId, genericName) => {
                                // You can now use both values here
                                // Example: await updateGenericName(genericNameId, genericName);
                                saveEdit(genericNameId, genericName);
                              }}
                              onCancel={cancelEdit}
                              trigger={
                                <button
                                  type="button"
                                  aria-label="Edit"
                                  onClick={() => startEdit("generic_name", gen)}
                                  className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                                >
                                  <UseAnimations
                                    animation={editIcon}
                                    size={22}
                                    style={{ color: "#2563eb" }}
                                  />
                                </button>
                              }
                            />
                            <Popover
                              open={!!genericDeletePopover[gen.generic_name_id]}
                              onOpenChange={(open) => {
                                setGenericDeletePopover((prev) => ({
                                  ...prev,
                                  [gen.generic_name_id]: open,
                                }));
                                if (!open) {
                                  setGenericDeleteInput((prev) => ({
                                    ...prev,
                                    [gen.generic_name_id]: "",
                                  }));
                                }
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Delete"
                                  className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                                  onClick={() =>
                                    setGenericDeletePopover((prev) => ({
                                      ...prev,
                                      [gen.generic_name_id]: true,
                                    }))
                                  }
                                >
                                  <UseAnimations
                                    animation={trash2}
                                    size={22}
                                    style={{ color: "#dc2626" }}
                                  />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72">
                                <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                  Type{" "}
                                  <span className="font-bold">
                                    {gen.generic_name_name}
                                  </span>{" "}
                                  to confirm delete:
                                </div>
                                <Input
                                  value={
                                    genericDeleteInput[gen.generic_name_id] ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    setGenericDeleteInput((prev) => ({
                                      ...prev,
                                      [gen.generic_name_id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Generic name"
                                  className="mb-2"
                                  autoFocus
                                />
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setGenericDeletePopover((prev) => ({
                                        ...prev,
                                        [gen.generic_name_id]: false,
                                      }));
                                      setGenericDeleteInput((prev) => ({
                                        ...prev,
                                        [gen.generic_name_id]: "",
                                      }));
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    disabled={
                                      genericDeleteInput[
                                        gen.generic_name_id
                                      ] !== gen.generic_name_name
                                    }
                                    onClick={async () => {
                                      if (
                                        gen.brand_drug_names &&
                                        gen.brand_drug_names.length > 0
                                      ) {
                                        toast.error(
                                          "Cannot delete generic name, please delete all associated brand drug names first and try again"
                                        );
                                        setGenericDeletePopover((prev) => ({
                                          ...prev,
                                          [gen.generic_name_id]: false,
                                        }));
                                        setGenericDeleteInput((prev) => ({
                                          ...prev,
                                          [gen.generic_name_id]: "",
                                        }));
                                      } else {
                                        try {
                                          await deleteGenericName(
                                            gen.generic_name_id
                                          );
                                          toast.success(
                                            "Generic name deleted successfully"
                                          );
                                          setGenericDeletePopover((prev) => ({
                                            ...prev,
                                            [gen.generic_name_id]: false,
                                          }));
                                          setGenericDeleteInput((prev) => ({
                                            ...prev,
                                            [gen.generic_name_id]: "",
                                          }));
                                          await fetchData();
                                        } catch (error) {
                                          toast.error(
                                            "Failed to delete generic name"
                                          );
                                        }
                                      }
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                            {/* AddBrandDrugModal for this generic name */}
                            {selectedGenericId === gen.generic_name_id && (
                              <AddBrandDrugModal
                                open={addBrandDrugModalOpen}
                                onOpenChange={(open) => {
                                  setAddBrandDrugModalOpen(open);
                                  if (!open) setSelectedGenericId(null);
                                }}
                                onSave={async (data) => {
                                  if (!selectedGenericId) return;
                                  try {
                                    await addNewBrandDrugName({
                                      brandDrugName: data.name,
                                      contents: data.contents,
                                      formulation: data.formulation,
                                      genericNameId: selectedGenericId,
                                    });
                                    toast.success(
                                      `Brand drug '${data.name}' added!`
                                    );
                                    setAddBrandDrugModalOpen(false);
                                    setSelectedGenericId(null);
                                    await fetchData();
                                  } catch (error) {
                                    toast.error("Failed to add brand drug");
                                  }
                                }}
                                onCancel={() => {
                                  setAddBrandDrugModalOpen(false);
                                  setSelectedGenericId(null);
                                }}
                                trigger={<></>}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Render brand drugs if expanded */}
                      {expandedGenerics.has(gen.generic_name_id) &&
                        gen.brand_drug_names.map((brand) => (
                          <TableRow
                            key={brand.brand_drug_id}
                            className="bg-gray-100 dark:bg-gray-900/10"
                          >
                            <TableCell className="py-2 px-12 font-semibold">
                              {brand.brand_drug_name}
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              {brand.brand_drug_last_used_at
                                ? dayjs(brand.brand_drug_last_used_at).format(
                                    "MMM D, YYYY"
                                  )
                                : "-"}
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              {brand.brand_drug_usage_count}
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              {brand.brand_drug_contents}
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              <Badge
                                variant="destructive"
                                className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200"
                              >
                                Brand Drug
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              <Badge
                                variant="outline"
                                className={
                                  brand.brand_drug_formulation === "TABLET"
                                    ? "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900 dark:text-purple-200"
                                    : brand.brand_drug_formulation === "CAPSULE"
                                    ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200"
                                    : brand.brand_drug_formulation === "SYRUP"
                                    ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200"
                                    : brand.brand_drug_formulation === "SACHET"
                                    ? "bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900 dark:text-pink-200"
                                    : brand.brand_drug_formulation ===
                                      "SUPPOSITORY"
                                    ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200"
                                    : brand.brand_drug_formulation ===
                                      "OINTMENT"
                                    ? "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200"
                                    : brand.brand_drug_formulation ===
                                      "LINIMENT"
                                    ? "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900 dark:text-teal-200"
                                    : brand.brand_drug_formulation ===
                                      "NASAL_SPRAY"
                                    ? "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900 dark:text-cyan-200"
                                    : "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200"
                                }
                              >
                                {brand.brand_drug_formulation}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              <div className="flex items-center gap-2">
                                <BrandDrugEditModal
                                  open={
                                    modalOpen &&
                                    editing?.type === "brand_drug" &&
                                    editing?.id === brand.brand_drug_id
                                  }
                                  onOpenChange={(open) => {
                                    setModalOpen(open);
                                    if (!open) cancelEdit();
                                  }}
                                  values={{
                                    brand_drug_name:
                                      editForm.brand_drug_name || "",
                                    brand_drug_contents:
                                      editForm.brand_drug_contents || "",
                                    brand_drug_formulation:
                                      editForm.brand_drug_formulation || "",
                                  }}
                                  brandDrugId={brand.brand_drug_id}
                                  onChange={handleEditChange}
                                  onSave={(brandDrugId, values) => {
                                    // You can now use both values here
                                    // Example: await updateBrandDrugName(brandDrugId, values);
                                    saveEdit(brandDrugId, values);
                                  }}
                                  onCancel={cancelEdit}
                                  trigger={
                                    <button
                                      type="button"
                                      aria-label="Edit"
                                      onClick={() =>
                                        startEdit("brand_drug", brand)
                                      }
                                      className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                                    >
                                      <UseAnimations
                                        animation={editIcon}
                                        size={22}
                                        style={{ color: "#2563eb" }}
                                      />
                                    </button>
                                  }
                                />
                                <Popover
                                  open={!!deletePopover[brand.brand_drug_id]}
                                  onOpenChange={(open) => {
                                    setDeletePopover((prev) => ({
                                      ...prev,
                                      [brand.brand_drug_id]: open,
                                    }));
                                    if (!open) {
                                      setDeleteInput((prev) => ({
                                        ...prev,
                                        [brand.brand_drug_id]: "",
                                      }));
                                    }
                                  }}
                                >
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      aria-label="Delete"
                                      className="bg-transparent border-none p-0 m-0 hover:opacity-80 focus:outline-none"
                                      onClick={() =>
                                        setDeletePopover((prev) => ({
                                          ...prev,
                                          [brand.brand_drug_id]: true,
                                        }))
                                      }
                                    >
                                      <UseAnimations
                                        animation={trash2}
                                        size={22}
                                        style={{ color: "#dc2626" }}
                                      />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72">
                                    <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                      Type{" "}
                                      <span className="font-bold">
                                        {brand.brand_drug_name}
                                      </span>{" "}
                                      to confirm delete:
                                    </div>
                                    <Input
                                      value={
                                        deleteInput[brand.brand_drug_id] || ""
                                      }
                                      onChange={(e) =>
                                        setDeleteInput((prev) => ({
                                          ...prev,
                                          [brand.brand_drug_id]: e.target.value,
                                        }))
                                      }
                                      placeholder="Brand drug name"
                                      className="mb-2"
                                      autoFocus
                                    />
                                    <div className="flex gap-2 justify-end">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setDeletePopover((prev) => ({
                                            ...prev,
                                            [brand.brand_drug_id]: false,
                                          }));
                                          setDeleteInput((prev) => ({
                                            ...prev,
                                            [brand.brand_drug_id]: "",
                                          }));
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        disabled={
                                          deleteInput[brand.brand_drug_id] !==
                                          brand.brand_drug_name
                                        }
                                        onClick={async () => {
                                          try {
                                            await deleteBrandDrug(
                                              brand.brand_drug_id
                                            );
                                            toast.success(
                                              "Brand drug deleted successfully"
                                            );
                                            setDeletePopover((prev) => ({
                                              ...prev,
                                              [brand.brand_drug_id]: false,
                                            }));
                                            setDeleteInput((prev) => ({
                                              ...prev,
                                              [brand.brand_drug_id]: "",
                                            }));
                                            await fetchData();
                                          } catch (error) {
                                            toast.error(
                                              "Failed to delete brand drug"
                                            );
                                          }
                                        }}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PrescriptionTable;
